/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { AnyItem, ItemType, Page } from '../../registry/ComponentRegistry';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { getManager } from '../../registry/Manager';
import { defaultValue } from './ActionOnTimeout';

const ActionOnTimeoutManager: ActionManagerFn<'ActionOnTimeout'> = (
  platform: Platform,
): ActionManager<'ActionOnTimeout'> => {
  return {
    ...baseActionManager(platform, 'ActionOnTimeout'),
    isChildAllowed: (page, item, childType) =>
      getManager(childType).managerType === 'Action',
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return (['ActionWaitFor'] as ItemType[]).includes(parent.type);
    },
    defaultValue,
    props: {
      ...baseActionProps,
    },
    name: 'On Timeout',
    icon: { iconName: 'alarm-clock' },
    description: `**Overview**<br/>
The "On Timeout" action is a specialized logic container designed to handle the "failure" case of a timed wait operation. It acts as the fallback execution path for the ActionWaitFor component, triggering only if the specified wait duration expires before the required condition is met.<br/><br/>
This component cannot exist independently; it is strictly an architectural element used to define "what happens next" when a process takes too long. It allows developers to implement error handling, default behaviors, or user feedback mechanisms for stalled or slow operations.

**Key Features**<br/>
* **Timeout Handler:** Explicitly designed to catch scenarios where a Wait For action exhausts its time limit, ensuring the application doesn't hang indefinitely without feedback.
* **Strict Parent Scope:** Enforces usage validity via isAllowedInsideParent. It can only be used as a child of the ActionWaitFor component.
* **Logic Grouping:** Functions as a generic container that allows any standard Action component to be added as a child (isChildAllowed), giving developers full flexibility to define the remediation strategy.
* **Zero Configuration:** Inherits all properties from baseActionManager and requires no additional inputs. Its trigger is solely determined by the parent's timer.

**Properties**<br/>
* **Base Properties:** This component utilizes ...baseActionProps exclusively, accepting only the standard configuration options available to all actions.

**Use Cases**<br/>
* **Error Feedback:** Showing a "Server is not responding" toast message (ActionShowMessage) if a wait for a data flag takes longer than 10 seconds.
* **State Reset:** Automatically hiding a loading spinner or enabling a button again if the expected background process fails to complete in time.
* **Fallback Logic:** Setting a default value for a variable if an external calculation fails to return a result within the allotted window.`,
  };
};

export default ActionOnTimeoutManager;
